import { useState, useMemo } from "react";
import { ColorBlock } from "@/components/page";
import { useResourceList } from "@/components/data";

type MarketingContact = {
  id: number;
  name: string;
  role: string | null;
  school: string | null;
  phone: string | null;
  category: string | null;
  note: string | null;
};

export function PaamonMarketingContactsPage() {
  const { data, isLoading } = useResourceList<MarketingContact>("paamon_marketing_contacts");
  const all = data ?? [];

  const categories = useMemo(() => {
    const s = new Set<string>();
    for (const c of all) if (c.category) s.add(c.category);
    return Array.from(s).sort();
  }, [all]);

  const [active, setActive] = useState<string>("all");
  const filtered = active === "all" ? all : all.filter((c) => c.category === active);

  // DEM-40: Layout owns chrome (sky-blue bg + max-w-[1000px] on /paamon).
  return (
    <div className="space-y-6 sm:space-y-7">
        {/* Hero */}
        <header className="pb-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">
            אנשי קשר שיווקיים
          </p>
          <h1 className="mt-3 flex items-baseline gap-3 text-2xl sm:text-3xl font-bold tracking-tight text-stone-900">
            <span className="tabular-nums" dir="ltr">{all.length}</span>
            <span className="text-stone-300 text-lg sm:text-xl">·</span>
            <span className="text-stone-700 text-lg sm:text-xl">מקורות</span>
          </h1>
          <p className="mt-3 max-w-xl text-sm sm:text-base text-stone-600 leading-snug">
            המלצות, פרסום ופרטנרים — מי מי בעולם השיווק של פעמון.
          </p>
        </header>

        {/* Pill tabs */}
        {categories.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <PillTab label="הכל" count={all.length} active={active === "all"} onClick={() => setActive("all")} />
            {categories.map((cat) => (
              <PillTab
                key={cat}
                label={cat}
                count={all.filter((c) => c.category === cat).length}
                active={active === cat}
                onClick={() => setActive(cat)}
              />
            ))}
          </div>
        )}

        {isLoading ? (
          <p className="text-sm text-stone-500">טוען…</p>
        ) : filtered.length === 0 ? (
          <ColorBlock tint="stone" className="text-center" size="lg">
            <p className="text-xl sm:text-2xl font-bold tracking-tight text-stone-900">אין כאן עדיין.</p>
          </ColorBlock>
        ) : (
          <div className="rounded-[24px] bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  {["שם", "תפקיד", "בית ספר", "טלפון", "קטגוריה", "הערה"].map((h) => (
                    <th key={h} className="text-start px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-t border-stone-200/70 hover:bg-stone-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-stone-900">{c.name}</td>
                    <td className="px-6 py-4 text-stone-600">{c.role ?? "—"}</td>
                    <td className="px-6 py-4 text-stone-600">{c.school ?? "—"}</td>
                    <td className="px-6 py-4 text-stone-600 tabular-nums" dir="ltr">{c.phone ?? "—"}</td>
                    <td className="px-6 py-4 text-stone-600">{c.category ?? "—"}</td>
                    <td className="px-6 py-4 text-xs text-stone-500">{c.note ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
  );
}

function PillTab({ label, count, active, onClick }: { label: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "inline-flex items-center gap-2 rounded-full h-10 px-5 text-sm font-medium transition-colors " +
        (active
          ? "bg-stone-900 text-stone-50"
          : "bg-stone-200/70 text-stone-700 hover:bg-stone-200")
      }
    >
      <span>{label}</span>
      <span
        className={
          "rounded-full px-2 py-0.5 text-[11px] tabular-nums " +
          (active ? "bg-stone-50/15 text-stone-50" : "bg-white/80 text-stone-600")
        }
        dir="ltr"
      >
        {count}
      </span>
    </button>
  );
}
